import type { UserStateEntry, UserStateMap } from '../../types/user-state';
import { isSavedJobKey, isViewedJobKey, isAppliedJobKey } from '../../types/user-state';
import type { ApplicationState, Job, JobsState } from '../../types/tracked-job';
import { isApplySource, isChecklistItem } from '../../types/tracked-job';
import { sanitizeMailInterviewDates, sanitizePlusTracker } from '../plus-myjobs/sanitize-tracker';

/**
 * Builds a `Job` from a single UserState entry (a `viewedJob#`/`savedJob#`/`appliedJob#` item) —
 * the single-entry counterpart to `extractJobsFromUserState` below (same field extraction, just
 * for one entry instead of scanning the whole map). Used wherever a specific job's tracked state
 * needs reading directly by itemKey, e.g. `resolve-tracked-job-ref.ts`.
 */
export function userStateEntryToJob(entry: UserStateEntry | null | undefined): Job | null {
  if (!entry || !entry.data) return null;
  const data = entry.data as Record<string, unknown>;
  const id = data.id ?? data.objectID;
  if (!id) return null;
  const job: Job = {
    id: String(id),
    title: data.title,
    normalisedTitle: data.normalisedTitle,
    companyId: data.companyId,
    companyName: typeof data.companyName === 'string' ? data.companyName : undefined,
  } as Job;
  if (
    data.applicationState !== undefined &&
    ['interviewing', 'offer', 'rejected', 'hired', null].includes(data.applicationState as ApplicationState)
  ) {
    job.applicationState = data.applicationState as ApplicationState;
  }
  if (data.isHidden === true) {
    job.isHidden = true;
  }
  if (typeof data.externalUrl === 'string' && data.externalUrl.length > 0) {
    job.externalUrl = data.externalUrl;
  }
  if (typeof data.applicationDeadline === 'string' && data.applicationDeadline.length > 0) {
    job.applicationDeadline = data.applicationDeadline;
  }
  if (typeof data.notes === 'string') {
    job.notes = data.notes;
  }
  if (Array.isArray(data.tags)) {
    job.tags = data.tags.filter((t): t is string => typeof t === 'string');
  }
  if (Array.isArray(data.checklist)) {
    job.checklist = data.checklist.filter(isChecklistItem);
  }
  if (isApplySource(data.applySource)) {
    job.applySource = data.applySource;
  }
  if (typeof data.nudgeSnoozedUntil === 'string' && data.nudgeSnoozedUntil.length > 0) {
    job.nudgeSnoozedUntil = data.nudgeSnoozedUntil;
  }
  const plusTracker = sanitizePlusTracker(data.plusTracker);
  if (plusTracker) {
    job.plusTracker = plusTracker;
  }
  const interviewDates = sanitizeMailInterviewDates(data.interviewDates);
  if (interviewDates) {
    job.interviewDates = interviewDates;
  }
  if (data.viewedAt) {
    (job as Record<string, unknown>).viewedAt = data.viewedAt;
  }
  if (data.appliedAt) {
    (job as Record<string, unknown>).appliedAt = data.appliedAt;
  }
  if (data.savedAt) {
    (job as Record<string, unknown>).savedAt = data.savedAt;
  }
  return job;
}

/**
 * Extract viewed/saved/applied jobs from a UserState map — the TanStack-side equivalent of
 * `redux/features/jobs/jobsSlice.ts`'s pre-TEC-404 SSR hydration helper (same name, same
 * behavior; moved here so it has a Redux-free home).
 */
export function extractJobsFromUserState(userState: UserStateMap): JobsState {
  const viewedJobs: Job[] = [];
  const savedJobs: Job[] = [];
  const appliedJobs: Job[] = [];

  Object.entries(userState).forEach(([itemKey, entry]) => {
    const entryData = entry.data as Record<string, unknown>;
    const jobId = entryData.id ?? entryData.objectID;

    if (!jobId) {
      return;
    }

    const job: Job = {
      id: String(jobId),
      title: entryData.title,
      normalisedTitle: entryData.normalisedTitle,
      companyId: entryData.companyId,
      companyName: typeof entryData.companyName === 'string' ? entryData.companyName : undefined,
    } as Job;

    // Extract application state from appliedJob# entry
    // applicationState is stored as a field in the appliedJob# entry (mutually exclusive, only applies to applied jobs)
    if (
      entryData.applicationState !== undefined &&
      ['interviewing', 'offer', 'rejected', 'hired', null].includes(entryData.applicationState as ApplicationState)
    ) {
      job.applicationState = entryData.applicationState as ApplicationState;
    }

    // Extract hidden flag
    if (entryData.isHidden === true) {
      job.isHidden = true;
    }

    // Extract timestamps
    if (entryData.viewedAt) {
      (job as Record<string, unknown>).viewedAt = entryData.viewedAt;
    }
    if (entryData.appliedAt) {
      (job as Record<string, unknown>).appliedAt = entryData.appliedAt;
    }
    if (entryData.savedAt) {
      (job as Record<string, unknown>).savedAt = entryData.savedAt;
    }
    if (typeof entryData.externalUrl === 'string' && entryData.externalUrl.length > 0) {
      job.externalUrl = entryData.externalUrl;
    }
    if (typeof entryData.applicationDeadline === 'string' && entryData.applicationDeadline.length > 0) {
      job.applicationDeadline = entryData.applicationDeadline;
    }
    if (typeof entryData.notes === 'string') {
      job.notes = entryData.notes;
    }
    if (Array.isArray(entryData.tags)) {
      job.tags = entryData.tags.filter((t): t is string => typeof t === 'string');
    }
    if (Array.isArray(entryData.checklist)) {
      job.checklist = entryData.checklist.filter(isChecklistItem);
    }
    if (isApplySource(entryData.applySource)) {
      job.applySource = entryData.applySource;
    }
    if (typeof entryData.nudgeSnoozedUntil === 'string' && entryData.nudgeSnoozedUntil.length > 0) {
      job.nudgeSnoozedUntil = entryData.nudgeSnoozedUntil;
    }
    const plusTracker = sanitizePlusTracker(entryData.plusTracker);
    if (plusTracker) {
      job.plusTracker = plusTracker;
    }
    const interviewDates = sanitizeMailInterviewDates(entryData.interviewDates);
    if (interviewDates) {
      job.interviewDates = interviewDates;
    }

    // Add to appropriate array based on itemKey
    if (isSavedJobKey(itemKey)) {
      savedJobs.push(job);
    }
    if (isViewedJobKey(itemKey)) {
      viewedJobs.push(job);
    }
    if (isAppliedJobKey(itemKey)) {
      appliedJobs.push(job);
    }
  });

  // Log summary only if there's a significant discrepancy
  const isServer = typeof window === 'undefined';
  const totalJobEntries = savedJobs.length + viewedJobs.length + appliedJobs.length;
  const totalUserStateEntries = Object.keys(userState).filter(k =>
    k.startsWith('savedJob#') || k.startsWith('viewedJob#') || k.startsWith('appliedJob#')
  ).length;

  if (isServer && totalJobEntries !== totalUserStateEntries) {
    console.warn('[extractJobsFromUserState] SSR: Job extraction mismatch:', {
      totalUserStateEntries,
      extractedJobs: totalJobEntries,
      savedCount: savedJobs.length,
      viewedCount: viewedJobs.length,
      appliedCount: appliedJobs.length,
    });
  }

  return {
    viewedJobs,
    savedJobs,
    appliedJobs,
  };
}
